/**
 * The translated copy a widget card shows when it has no rows or could not run.
 *
 * `@aimsos/ui` ships English defaults frozen into `DEFAULT_STATE_COPY`, which
 * is why a Spanish session used to read "No data to display." on every empty
 * card (CT-5885). `WidgetGridProvider` now takes a `stateCopy` prop and lays
 * whatever we pass over its own defaults, per state — so this only has to
 * carry the strings, not the icons or the button variants.
 *
 * Keeping the two error states apart matters as much as translating them: a
 * widget that found nothing is a normal answer, and one that could not run is
 * a failure the author has to act on. They used to read identically.
 */

import { useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';

export function useTranslatedWidgetStateCopy() {
  // 'dashboards', like every other hook here: the app is hosted inside
  // core-shell, whose `defaultNS` is its own, so an unnamespaced `t` would
  // not resolve `widgetStates.*` and the cards would show the raw keys.
  const { t } = useTranslation('dashboards');

  return useMemo(
    () => ({
      EMPTY: {
        title: t('widgetStates.emptyTitle'),
        message: t('widgetStates.emptyMessage'),
      },
      CONNECTION_ERROR: {
        title: t('widgetStates.connectionTitle'),
        message: t('widgetStates.connectionMessage'),
        buttonText: t('widgetStates.reload'),
      },
      SYSTEM_ERROR: {
        title: t('widgetStates.systemTitle'),
        message: t('widgetStates.systemMessage'),
        buttonText: t('widgetStates.retry'),
      },
    }),
    [t],
  );
}

export default useTranslatedWidgetStateCopy;
